import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import usePermissions from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../config/permissions';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const PASSTHROUGH_TYPES = ['agm', 'hotel', 'mcpd', 'form'];

export default function AdminCustomerLedger() {
  const { canEdit, canAdd, canDelete } = usePermissions();
  const [members, setMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Add/Edit entry
  const [showEntryForm, setShowEntryForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [entryForm, setEntryForm] = useState({
    date: new Date().toISOString().split('T')[0],
    transaction_number: '',
    entry_type: 'SJ',
    payment_type: 'annual_dues',
    description: '',
    debit_amount: '',
    credit_amount: '',
    passthrough: false,
  });
  const [saving, setSaving] = useState(false);

  // Delete
  const [deleteEntry, setDeleteEntry] = useState(null);

  // Date range filter
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Fetch members for search
  useEffect(() => {
    api.get('/accounts/admin/members/?page_size=1000').then(res => {
      setMembers(res.data.results || res.data);
    });
  }, []);

  const filteredMembers = members.filter(m => {
    const q = memberSearch.trim().toLowerCase();
    if (!q) return true;
    const surname = (m.surname || m.last_name || m.user?.last_name || '').toLowerCase();
    const otherNames = (m.other_names || m.first_name || m.user?.first_name || '').toLowerCase();
    const fullName = (m.full_name || '').toLowerCase();
    const num = (m.membership_number || '').toLowerCase();
    const email = (m.email || m.user?.email || '').toLowerCase();
    const haystack = `${surname} ${otherNames} ${otherNames} ${surname} ${fullName} ${num} ${email}`;
    const words = q.split(/\s+/);
    return haystack.includes(q) || words.every(w => haystack.includes(w));
  });

  const fetchLedger = useCallback(async (memberId) => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/ledger/member/${memberId}/`);
      setLedger(res.data);
    } catch {
      toast.error('Failed to load ledger.');
    } finally {
      setLoading(false);
    }
  }, []);

  const selectMember = (m) => {
    setSelectedMember(m);
    setMemberSearch(m.full_name || '');
    setShowDropdown(false);
    fetchLedger(m.id);
  };

  // Reset form
  const resetEntryForm = () => {
    setEntryForm({
      date: new Date().toISOString().split('T')[0],
      transaction_number: '',
      entry_type: 'SJ',
      payment_type: 'annual_dues',
      description: '',
      debit_amount: '',
      credit_amount: '',
      passthrough: false,
    });
    setEditingEntry(null);
    setShowEntryForm(false);
  };

  // Open edit
  const openEditEntry = (entry) => {
    const isPassthrough = entry.entry_type === 'CRJ' && Number(entry.debit_amount) > 0 && Number(entry.debit_amount) === Number(entry.credit_amount);
    setEditingEntry(entry);
    setEntryForm({
      date: entry.date,
      transaction_number: entry.transaction_number,
      entry_type: entry.entry_type,
      payment_type: entry.payment_type || 'annual_dues',
      description: entry.description,
      debit_amount: entry.debit_amount,
      credit_amount: entry.credit_amount,
      passthrough: isPassthrough,
    });
    setShowEntryForm(true);
  };

  // Save entry (create or update)
  const handleSaveEntry = async (e) => {
    e.preventDefault();
    if (!selectedMember) return;
    setSaving(true);
    try {
      const payload = {
        ...entryForm,
        member: selectedMember.id,
        debit_amount: entryForm.debit_amount || 0,
        credit_amount: entryForm.entry_type === 'CRJ'
          ? (entryForm.passthrough ? (entryForm.debit_amount || 0) : (entryForm.credit_amount || 0))
          : 0,
      };

      if (editingEntry) {
        await api.put(`/admin/ledger/${editingEntry.id}/`, payload);
        toast.success('Entry updated!');
      } else {
        await api.post('/admin/ledger/', payload);
        toast.success('Entry added!');
      }
      resetEntryForm();
      fetchLedger(selectedMember.id);
    } catch {
      toast.error('Failed to save entry.');
    } finally {
      setSaving(false);
    }
  };

  // Delete entry
  const handleDeleteEntry = async () => {
    if (!deleteEntry) return;
    try {
      await api.delete(`/admin/ledger/${deleteEntry.id}/`);
      toast.success('Entry deleted.');
      setDeleteEntry(null);
      fetchLedger(selectedMember.id);
    } catch {
      toast.error('Failed to delete entry.');
    }
  };

  // Download PDF
  const downloadLedgerPDF = async () => {
    if (!ledger) return;
    try {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;
    const usableWidth = pageWidth - margin * 2; // ~269mm

    // Try to load logo
    try {
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        logoImg.onload = resolve;
        logoImg.onerror = reject;
        logoImg.src = '/NIS ABJ LOGO.jpg';
      });
      doc.addImage(logoImg, 'JPEG', margin, 8, 22, 22);
    } catch {}

    // Title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('NIGERIAN INSTITUTION OF SURVEYORS', 40, 15);
    doc.setFontSize(12);
    doc.text('FCT BRANCH — Member Ledger', 40, 22);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Customer ID: ${ledger.membership_number || 'N/A'}`, margin, 38);
    doc.text(`Customer Name: ${ledger.member_name}`, margin, 44);

    // Date range line — only show if dates are selected
    let nextY = 50;
    if (dateFrom || dateTo) {
      let dateRangeText = '';
      if (dateFrom && dateTo) {
        dateRangeText = `Period: ${dateFrom} to ${dateTo}`;
      } else if (dateFrom) {
        dateRangeText = `Period: From ${dateFrom}`;
      } else {
        dateRangeText = `Period: Up to ${dateTo}`;
      }
      doc.text(dateRangeText, margin, nextY);
      nextY += 6;
    }
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, margin, nextY);
    const tableStartY = nextY + 6;

    // Filter entries by date range
    const filteredEntries = ledger.entries.filter(e => {
      if (dateFrom && e.date < dateFrom) return false;
      if (dateTo && e.date > dateTo) return false;
      return true;
    });
    const fDebit = filteredEntries.reduce((s, e) => s + Number(e.debit_amount || 0), 0);
    const fCredit = filteredEntries.reduce((s, e) => s + Number(e.credit_amount || 0), 0);
    const fBalance = fDebit - fCredit;

    // Column widths (proportional to usable width)
    const colDate = 28;
    const colTrans = 28;
    const colType = 20;
    const colDebit = 36;
    const colCredit = 36;
    const colBalance = 36;
    const colDesc = usableWidth - colDate - colTrans - colType - colDebit - colCredit - colBalance;

    // Build body rows from filtered entries
    const bodyRows = filteredEntries.map(e => {
      return [
        e.date,
        e.transaction_number,
        e.entry_type,
        e.description,
        e.debit_amount !== '0.00' ? `N${Number(e.debit_amount).toLocaleString()}` : '',
        e.credit_amount !== '0.00' ? `N${Number(e.credit_amount).toLocaleString()}` : '',
        `N${Math.abs(Number(e.balance)).toLocaleString()}`,
      ];
    });

    // Total row using filtered totals
    const totalBalance = (dateFrom || dateTo) ? fBalance : Number(ledger.balance);
    const totalDebit = (dateFrom || dateTo) ? fDebit : Number(ledger.total_debit);
    const totalCredit = (dateFrom || dateTo) ? fCredit : Number(ledger.total_credit);
    const totalBalanceStr = totalBalance > 0
      ? `-N${totalBalance.toLocaleString()}`
      : `N${Math.abs(totalBalance).toLocaleString()}`;
    bodyRows.push([
      { content: '', styles: {} },
      { content: '', styles: {} },
      { content: '', styles: {} },
      { content: 'Report Total', styles: { fontStyle: 'bold', halign: 'right' } },
      { content: `N${totalDebit.toLocaleString()}`, styles: { fontStyle: 'bold', halign: 'right' } },
      { content: `N${totalCredit.toLocaleString()}`, styles: { fontStyle: 'bold', halign: 'right' } },
      { content: totalBalanceStr, styles: { fontStyle: 'bold', halign: 'right' } },
    ]);

    // Table
    autoTable(doc, {
      startY: tableStartY,
      margin: { left: margin, right: margin },
      tableWidth: usableWidth,
      head: [['Date', 'Trans No', 'Type', 'Description', 'Debit Amt', 'Credit Amt', 'Balance']],
      body: bodyRows,
      styles: { fontSize: 10, cellPadding: 3, overflow: 'linebreak', lineColor: [200, 200, 200], lineWidth: 0.2 },
      headStyles: { fillColor: [11, 93, 30], textColor: 255, fontStyle: 'bold', halign: 'center', cellPadding: 4 },
      alternateRowStyles: { fillColor: [248, 248, 248] },
      columnStyles: {
        0: { cellWidth: colDate, halign: 'center' },
        1: { cellWidth: colTrans, halign: 'center' },
        2: { cellWidth: colType, halign: 'center' },
        3: { cellWidth: colDesc, halign: 'left' },
        4: { cellWidth: colDebit, halign: 'right' },
        5: { cellWidth: colCredit, halign: 'right' },
        6: { cellWidth: colBalance, halign: 'right' },
      },
      didParseCell: (data) => {
        const isLastRow = data.row.index === bodyRows.length - 1;

        // Debit column (col 4) — red text
        if (data.column.index === 4 && data.cell.raw !== '') {
          data.cell.styles.textColor = [220, 38, 38];
        }
        // Credit column (col 5) — green text
        if (data.column.index === 5 && data.cell.raw !== '') {
          data.cell.styles.textColor = [22, 163, 74];
        }
        // Balance column (col 6) — red if debit, green if credit
        if (data.column.index === 6 && data.section === 'body') {
          if (isLastRow) {
            data.cell.styles.textColor = totalBalance > 0 ? [220, 38, 38] : [22, 163, 74];
          } else {
            const entryBalance = Number(filteredEntries[data.row.index]?.balance || 0);
            data.cell.styles.textColor = entryBalance > 0 ? [220, 38, 38] : [22, 163, 74];
          }
        }

        // Style the total row — grey background, bold, top border only
        if (isLastRow) {
          data.cell.styles.fillColor = [235, 235, 235];
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });

    doc.save(`Ledger_${ledger.membership_number || ledger.member_name}.pdf`);
    } catch (err) {
      console.error('PDF generation error:', err);
      toast.error('Failed to generate PDF. Check console for details.');
    }
  };

  // === Export to Excel ===
  const exportLedgerExcel = () => {
    if (!ledger) return;
    try {

    // Date range header rows — only show if dates selected
    const headerRows = [
      { 'Date': 'NIGERIAN INSTITUTION OF SURVEYORS — FCT BRANCH', 'Trans No': '', 'Type': '', 'Description': '', 'Debit Amt': '', 'Credit Amt': '', 'Balance': '' },
      { 'Date': `Customer ID: ${ledger.membership_number || 'N/A'}`, 'Trans No': '', 'Type': '', 'Description': `Customer Name: ${ledger.member_name}`, 'Debit Amt': '', 'Credit Amt': '', 'Balance': '' },
    ];
    if (dateFrom || dateTo) {
      let dateRangeText = '';
      if (dateFrom && dateTo) {
        dateRangeText = `Period: ${dateFrom} to ${dateTo}`;
      } else if (dateFrom) {
        dateRangeText = `Period: From ${dateFrom}`;
      } else {
        dateRangeText = `Period: Up to ${dateTo}`;
      }
      headerRows.push({ 'Date': dateRangeText, 'Trans No': '', 'Type': '', 'Description': '', 'Debit Amt': '', 'Credit Amt': '', 'Balance': '' });
    }
    headerRows.push({ 'Date': `Generated: ${new Date().toLocaleDateString()}`, 'Trans No': '', 'Type': '', 'Description': '', 'Debit Amt': '', 'Credit Amt': '', 'Balance': '' });
    headerRows.push({ 'Date': '', 'Trans No': '', 'Type': '', 'Description': '', 'Debit Amt': '', 'Credit Amt': '', 'Balance': '' });

    // Filter entries by date range
    const filteredEntries = ledger.entries.filter(e => {
      if (dateFrom && e.date < dateFrom) return false;
      if (dateTo && e.date > dateTo) return false;
      return true;
    });
    const fDebit = filteredEntries.reduce((s, e) => s + Number(e.debit_amount || 0), 0);
    const fCredit = filteredEntries.reduce((s, e) => s + Number(e.credit_amount || 0), 0);
    const fBalance = fDebit - fCredit;
    const exTotalDebit = (dateFrom || dateTo) ? fDebit : Number(ledger.total_debit);
    const exTotalCredit = (dateFrom || dateTo) ? fCredit : Number(ledger.total_credit);
    const exTotalBalance = (dateFrom || dateTo) ? fBalance : Number(ledger.balance);

    const rows = filteredEntries.map(e => ({
      'Date': e.date,
      'Trans No': e.transaction_number,
      'Type': e.entry_type,
      'Description': e.description,
      'Debit Amt': Number(e.debit_amount) || 0,
      'Credit Amt': Number(e.credit_amount) || 0,
      'Balance': `₦${Math.abs(Number(e.balance)).toLocaleString()}`,
    }));
    rows.push({ 'Date': '', 'Trans No': '', 'Type': '', 'Description': 'REPORT TOTAL', 'Debit Amt': exTotalDebit, 'Credit Amt': exTotalCredit, 'Balance': exTotalBalance > 0 ? `-₦${exTotalBalance.toLocaleString()}` : `₦${Math.abs(exTotalBalance).toLocaleString()}` });
    const ws = XLSX.utils.json_to_sheet([...headerRows, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ledger');
    XLSX.writeFile(wb, `Ledger_${ledger.membership_number || ledger.member_name}.xlsx`);
    } catch (err) {
      console.error('Excel export error:', err);
      toast.error('Failed to export Excel. Check console for details.');
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Member Ledger</h1>
        <p className="text-sm text-gray-500">View and manage member financial ledgers</p>
      </div>

      {/* Member Search + Date Range */}
      <div className="bg-white rounded-xl shadow-sm border p-5 mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Search Member</label>
            <div className="relative" ref={searchRef}>
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={memberSearch}
                  onChange={(e) => { setMemberSearch(e.target.value); setShowDropdown(true); }}
                  onFocus={() => setShowDropdown(true)}
                  placeholder="Type member name or membership number..."
                  className="w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm"
                />
              {showDropdown && memberSearch && (
                <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {filteredMembers.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-400">No members found</div>
              ) : filteredMembers.slice(0, 10).map(m => (
                <button
                  key={m.id}
                  onClick={() => selectMember(m)}
                  className="w-full text-left px-4 py-2.5 hover:bg-green-50 flex justify-between items-center border-b last:border-b-0"
                >
                  <div>
                    <div className="text-sm font-medium text-gray-900">{m.full_name}</div>
                    <div className="text-xs text-gray-500">{m.membership_number || 'No ID'}</div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    m.membership_status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>{m.membership_status}</span>
                </button>
              ))}
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">From Date</label>
            <input type="date" value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">To Date</label>
            <input type="date" value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
          </div>
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(''); setDateTo(''); }}
              className="px-4 py-2.5 border rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition whitespace-nowrap">
              Clear
            </button>
          )}
        </div>
        {(dateFrom || dateTo) && selectedMember && ledger && (
          <p className="text-xs text-gray-500 mt-3">Showing {ledger.entries.filter(e => { if (dateFrom && e.date < dateFrom) return false; if (dateTo && e.date > dateTo) return false; return true; }).length} of {ledger.entries.length} entries</p>
        )}
      </div>

      {/* Ledger Content */}
      {loading && (
        <div className="text-center py-12 text-gray-500">Loading ledger...</div>
      )}

      {!loading && selectedMember && ledger && (
        <>
          {/* Member Info + Actions */}
          <div className="bg-white rounded-xl shadow-sm border p-5 mb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">{ledger.member_name}</h2>
                <p className="text-sm text-gray-500">Customer ID: <span className="font-mono font-semibold">{ledger.membership_number || 'N/A'}</span></p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {canAdd(PERMISSIONS.ADD_MEMBER_LEDGER) && (
                  <button
                    onClick={() => { resetEntryForm(); setShowEntryForm(true); }}
                    className="bg-[#0B5D1E] text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition flex items-center gap-1.5"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                    Add Entry
                  </button>
                )}
                <button
                  onClick={downloadLedgerPDF}
                  className="bg-purple-600 text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-purple-700 transition flex items-center gap-1.5"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17v3a2 2 0 002 2h14a2 2 0 002-2v-3" /></svg>
                  Download PDF
                </button>
                <button
                  onClick={exportLedgerExcel}
                  className="bg-green-700 text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-green-800 transition flex items-center gap-1.5"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  Export Excel
                </button>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          {(() => {
            const filtered = ledger.entries.filter(e => {
              if (dateFrom && e.date < dateFrom) return false;
              if (dateTo && e.date > dateTo) return false;
              return true;
            });
            const fDebit = filtered.reduce((s, e) => s + Number(e.debit_amount || 0), 0);
            const fCredit = filtered.reduce((s, e) => s + Number(e.credit_amount || 0), 0);
            const fBalance = fDebit - fCredit;
            const isFiltering = dateFrom || dateTo;
            return (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <p className="text-xs text-gray-500 font-semibold uppercase">Total Debit{isFiltering ? ' (Filtered)' : ''}</p>
              <p className="text-xl font-bold text-red-600">₦{(isFiltering ? fDebit : Number(ledger.total_debit)).toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <p className="text-xs text-gray-500 font-semibold uppercase">Total Credit{isFiltering ? ' (Filtered)' : ''}</p>
              <p className="text-xl font-bold text-green-600">₦{(isFiltering ? fCredit : Number(ledger.total_credit)).toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <p className="text-xs text-gray-500 font-semibold uppercase">Balance{isFiltering ? ' (Filtered)' : ''}</p>
              <p className={`text-xl font-bold ${(() => { const b = isFiltering ? fBalance : Number(ledger.balance); return b > 0 ? 'text-red-600' : 'text-green-600'; })()}`}>
                {(() => { const b = isFiltering ? fBalance : Number(ledger.balance); return b > 0 ? `-₦${b.toLocaleString()}` : `₦${Math.abs(b).toLocaleString()}`; })()}
              </p>
            </div>
          </div>
            );
          })()}

          {/* Add/Edit Entry Form */}
          {showEntryForm && (
            <div className="bg-white rounded-xl shadow-sm border p-5 mb-4">
              <h3 className="text-sm font-bold text-gray-800 mb-4">{editingEntry ? 'Edit Entry' : 'New Ledger Entry'}</h3>
              <form onSubmit={handleSaveEntry}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Date</label>
                    <input type="date" required value={entryForm.date}
                      onChange={(e) => setEntryForm({ ...entryForm, date: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
                    <select value={entryForm.entry_type}
                      onChange={(e) => setEntryForm({ ...entryForm, entry_type: e.target.value, passthrough: false })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm">
                      <option value="SJ">SJ — Sales Journal (Debit/Charge)</option>
                      <option value="CRJ">CRJ — Cash Receipt Journal (Credit/Payment)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Payment Type</label>
                    <select value={entryForm.payment_type}
                      onChange={(e) => {
                        const pt = e.target.value;
                        const autoPass = PASSTHROUGH_TYPES.includes(pt);
                        setEntryForm({ ...entryForm, payment_type: pt, passthrough: autoPass, entry_type: autoPass ? 'CRJ' : entryForm.entry_type });
                      }}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm">
                      <option value="annual_dues">Annual Dues</option>
                      <option value="registration">Registration</option>
                      <option value="event">Event Fee</option>
                      <option value="agm">AGM</option>
                      <option value="mcpd">MCPD</option>
                      <option value="form">Form</option>
                      <option value="hotel">Hotel</option>
                      <option value="penalty">Penalty</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className={entryForm.entry_type === 'SJ' && !entryForm.passthrough ? '' : ''}>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
                    <input type="text" value={entryForm.description}
                      onChange={(e) => setEntryForm({ ...entryForm, description: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm"
                      placeholder="e.g. Annual Dues 2024" />
                  </div>
                  {/* Pass-through toggle for CRJ */}
                  {entryForm.entry_type === 'CRJ' && (
                    <div className="md:col-span-3 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      <input type="checkbox" id="ledger-passthrough" checked={!!entryForm.passthrough}
                        onChange={(e) => setEntryForm({ ...entryForm, passthrough: e.target.checked })}
                        className="w-4 h-4 accent-[#0B5D1E] rounded" />
                      <label htmlFor="ledger-passthrough" className="text-xs text-amber-800">
                        <strong>Pass-through</strong> — Debit &amp; Credit equal, <em>no effect on balance</em> (e.g. AGM, Hotel)
                      </label>
                    </div>
                  )}
                  {entryForm.entry_type === 'CRJ' && entryForm.passthrough ? (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Amount (₦) — <span className="text-amber-600">Debit = Credit</span></label>
                      <input type="number" step="0.01" required
                        value={entryForm.debit_amount}
                        onChange={(e) => setEntryForm({ ...entryForm, debit_amount: e.target.value, credit_amount: e.target.value })}
                        className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-400 outline-none text-sm bg-amber-50"
                        placeholder="0.00" />
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Debit Amount (₦)</label>
                        <input type="number" step="0.01" required={entryForm.entry_type === 'SJ'}
                          value={entryForm.debit_amount}
                          onChange={(e) => setEntryForm({ ...entryForm, debit_amount: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm"
                          placeholder="0.00" />
                      </div>
                      {entryForm.entry_type === 'CRJ' && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Credit Amount (₦)</label>
                          <input type="number" step="0.01" required
                            value={entryForm.credit_amount}
                            onChange={(e) => setEntryForm({ ...entryForm, credit_amount: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm"
                            placeholder="0.00" />
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="flex gap-2 mt-4">
                  <button type="submit" disabled={saving}
                    className="bg-[#0B5D1E] text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition disabled:opacity-50">
                    {saving ? 'Saving...' : (editingEntry ? 'Update' : 'Add Entry')}
                  </button>
                  <button type="button" onClick={resetEntryForm}
                    className="px-6 py-2 rounded-lg text-sm font-semibold border hover:bg-gray-50 transition">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Ledger Table */}
          <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Trans No</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Description</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Debit Amt</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Credit Amt</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Balance</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {(() => {
                  const displayEntries = ledger.entries.filter(e => {
                    if (dateFrom && e.date < dateFrom) return false;
                    if (dateTo && e.date > dateTo) return false;
                    return true;
                  });
                  const isFiltering = dateFrom || dateTo;
                  const filteredDebit = displayEntries.reduce((s, e) => s + Number(e.debit_amount || 0), 0);
                  const filteredCredit = displayEntries.reduce((s, e) => s + Number(e.credit_amount || 0), 0);
                  return displayEntries.length === 0 ? (
                  <tr><td colSpan="8" className="text-center py-8 text-gray-400 text-sm">{ledger.entries.length > 0 ? 'No entries in selected date range.' : 'No ledger entries yet.'}</td></tr>
                ) : (
                  <>
                    {displayEntries.map((entry) => (
                      <tr key={entry.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{entry.date}</td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-700">{entry.transaction_number}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded font-semibold ${
                            entry.entry_type === 'SJ' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                          }`}>{entry.entry_type}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{entry.description}</td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-red-600">
                          {entry.debit_amount !== '0.00' ? `₦${Number(entry.debit_amount).toLocaleString()}` : ''}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-green-600">
                          {entry.credit_amount !== '0.00' ? `₦${Number(entry.credit_amount).toLocaleString()}` : ''}
                        </td>
                        <td className={`px-4 py-3 text-sm text-right font-bold ${Number(entry.balance) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          ₦{Math.abs(Number(entry.balance)).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex justify-center gap-1">
                            {/* Edit */}
                            {canEdit(PERMISSIONS.EDIT_MEMBER_LEDGER) && (
                              <button onClick={() => openEditEntry(entry)} title="Edit"
                                className="p-1.5 rounded hover:bg-blue-50 text-blue-600">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                            )}
                            {/* Delete */}
                            {canDelete(PERMISSIONS.DELETE_MEMBER_LEDGER) && (
                              <button onClick={() => setDeleteEntry(entry)} title="Delete"
                                className="p-1.5 rounded hover:bg-red-50 text-red-600">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {/* Report Total Row */}
                    <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                      <td colSpan="4" className="px-4 py-3 text-sm text-right text-gray-700">Report Total{isFiltering ? ' (Filtered)' : ''}</td>
                      <td className="px-4 py-3 text-sm text-right text-red-700">₦{(isFiltering ? filteredDebit : Number(ledger.total_debit)).toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-right text-green-700">₦{(isFiltering ? filteredCredit : Number(ledger.total_credit)).toLocaleString()}</td>
                      <td className={`px-4 py-3 text-sm text-right ${(() => { const b = isFiltering ? (filteredDebit - filteredCredit) : Number(ledger.balance); return b > 0 ? 'text-red-600' : 'text-green-600'; })()}`}>{(() => { const b = isFiltering ? (filteredDebit - filteredCredit) : Number(ledger.balance); return b > 0 ? `-₦${b.toLocaleString()}` : `₦${Math.abs(b).toLocaleString()}`; })()}</td>
                      <td></td>
                    </tr>
                  </>
                );
                })()}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading && !selectedMember && (
        <div className="text-center py-16 text-gray-400">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p className="text-lg font-medium">Search for a member to view their ledger</p>
          <p className="text-sm mt-1">Type a name or membership number above</p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteEntry && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDeleteEntry(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Entry?</h3>
            <p className="text-sm text-gray-500 mb-4">
              This will permanently remove the ledger entry dated <strong>{deleteEntry.date}</strong>.
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setDeleteEntry(null)}
                className="px-5 py-2 rounded-lg border text-sm font-semibold hover:bg-gray-50">Cancel</button>
              <button onClick={handleDeleteEntry}
                className="px-5 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
